import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, ArrowRight, Loader, MessageSquare, Phone, Mail,
  Sparkles, Users, Building2, Clock, Zap, CalendarCheck,
} from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Contact() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const [step, setStep]     = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const COUNTRIES = [
    { value: 'ae', labelEn: 'United Arab Emirates', labelAr: 'الإمارات العربية المتحدة', flag: '🇦🇪' },
    { value: 'sa', labelEn: 'Saudi Arabia',          labelAr: 'المملكة العربية السعودية',  flag: '🇸🇦' },
    { value: 'kw', labelEn: 'Kuwait',                labelAr: 'الكويت',                    flag: '🇰🇼' },
    { value: 'qa', labelEn: 'Qatar',                 labelAr: 'قطر',                       flag: '🇶🇦' },
    { value: 'bh', labelEn: 'Bahrain',               labelAr: 'البحرين',                   flag: '🇧🇭' },
    { value: 'om', labelEn: 'Oman',                  labelAr: 'عُمان',                     flag: '🇴🇲' },
    { value: 'eg', labelEn: 'Egypt',                 labelAr: 'مصر',                       flag: '🇪🇬' },
    { value: 'ma', labelEn: 'Morocco',               labelAr: 'المغرب',                    flag: '🇲🇦' },
    { value: 'sd', labelEn: 'Sudan',                 labelAr: 'السودان',                   flag: '🇸🇩' },
    { value: 'et', labelEn: 'Ethiopia',              labelAr: 'إثيوبيا',                   flag: '🇪🇹' },
    { value: 'ke', labelEn: 'Kenya',                 labelAr: 'كينيا',                     flag: '🇰🇪' },
    { value: 'tz', labelEn: 'Tanzania',              labelAr: 'تنزانيا',                   flag: '🇹🇿' },
    { value: 'ug', labelEn: 'Uganda',                labelAr: 'أوغندا',                    flag: '🇺🇬' },
    { value: 'rw', labelEn: 'Rwanda',                labelAr: 'رواندا',                    flag: '🇷🇼' },
    { value: 'other', labelEn: 'Other',              labelAr: 'دولة أخرى',                 flag: '🌍' },
  ];

  const [form, setForm]     = useState({
    name: '', company: '', email: '', phone: '', industry: '', country: '', message: '',
    contactType: 'demo',
  });
  const [errors, setErrors] = useState({});

  const contactTypes = [
    { key: 'demo',    icon: Sparkles,      labelEn: 'Request a Demo',    labelAr: 'طلب عرض توضيحي' },
    { key: 'inquiry', icon: MessageSquare, labelEn: 'General Inquiry',   labelAr: 'استفسار عام'    },
    { key: 'partner', icon: Users,         labelEn: 'Partnership',       labelAr: 'شراكة'          },
  ];

  const industries = [
    { key: 'hvac',          icon: Building2, labelEn: 'HVAC',                labelAr: 'التكييف'      },
    { key: 'mep',           icon: Building2, labelEn: 'MEP Engineering',     labelAr: 'MEP'          },
    { key: 'manufacturing', icon: Building2, labelEn: 'Industrial Suppliers',labelAr: 'الموردون'     },
    { key: 'tender',        icon: Building2, labelEn: 'Tender Teams',        labelAr: 'فرق المناقصات'},
    { key: 'sme',           icon: Building2, labelEn: 'SME Business',        labelAr: 'الشركات الصغيرة'},
  ];

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name  = true;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
    if (!form.message.trim()) e.message = true;
    return e;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, type: form.contactType, language: lang, source: 'contact_page' }),
      });
      setStep('success');
    } catch {
      setStep('success');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none focus:ring-2 focus:ring-primary/20 ${
      errors[field]
        ? 'border-red-300 bg-red-50/50 focus:border-red-400'
        : 'border-slate-200 bg-white focus:border-primary hover:border-slate-300'
    } text-slate-800 placeholder:text-slate-400`;

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <AIAssistant source="contact" />

      <div className="min-h-screen flex flex-col lg:flex-row -mt-16">

        {/* ── LEFT PANEL ── */}
        <div className="lg:w-[38%] bg-midnight relative flex flex-col pt-24 sm:pt-28 pb-10 sm:pb-12 px-6 sm:px-8 lg:px-12 lg:min-h-screen lg:sticky lg:top-0">
          {/* Background grid */}
          <div className="absolute inset-0 blueprint-grid-dark opacity-30 pointer-events-none" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[360px] h-[260px] bg-primary/10 blur-[100px] rounded-full pointer-events-none" />

          <div className="relative z-10 flex flex-col h-full">

            {/* Logo */}
            <div className="flex items-center gap-2.5 mb-10">
              <img src="/tuly.png" alt="Tuly" className="h-8 w-auto" />
              <span className="text-white font-bold text-base tracking-wide">Tuly Technology</span>
            </div>

            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-primary text-xs font-bold uppercase tracking-[0.18em] mb-3">
                {lang === 'ar' ? 'ابدأ المحادثة' : 'Start a Conversation'}
              </p>
              <h1 className="text-3xl lg:text-4xl font-black text-white leading-tight">
                {lang === 'ar'
                  ? 'دعنا نعرض لك\nما تقدر عليه تولي'
                  : "Let's show you\nwhat Tuly can do"}
              </h1>
              <p className="mt-4 text-white/50 text-sm leading-relaxed">
                {lang === 'ar'
                  ? 'فريقنا جاهز لإجراء عرض توضيحي مخصص لسير عمل قطاعك.'
                  : 'Our team is ready to run a tailored demo built around your workflow.'}
              </p>
            </motion.div>

            {/* Divider */}
            <div className="mt-8 mb-8 border-t border-white/10" />

            {/* Contact details */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-4"
            >
              {[
                { icon: Mail,  value: 'info@tuly.app',     href: 'mailto:info@tuly.app'    },
                { icon: Phone, value: '+971 566 7765 72',  href: 'tel:+971566776572'        },
              ].map((item, i) => (
                <a
                  key={i}
                  href={item.href}
                  className="flex items-center gap-3 group"
                >
                  <div className="w-9 h-9 rounded-xl bg-white/8 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 group-hover:border-primary/30 transition-all">
                    <item.icon size={15} className="text-white/60 group-hover:text-primary transition-colors" />
                  </div>
                  <span className="text-white/60 text-sm group-hover:text-white transition-colors">{item.value}</span>
                </a>
              ))}
            </motion.div>

            {/* Divider */}
            <div className="mt-8 mb-8 border-t border-white/10" />

            {/* What to expect */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex-1"
            >
              <p className="text-white/35 text-[11px] uppercase tracking-[0.15em] font-bold mb-5">
                {lang === 'ar' ? 'ماذا يحدث بعد ذلك' : 'What happens next'}
              </p>
              <ol className="space-y-5">
                {(lang === 'ar'
                  ? [
                      { icon: Zap,          text: 'تأكيد استلام طلبك خلال ساعة واحدة'         },
                      { icon: CalendarCheck, text: 'مكالمة اكتشافية مع أحد متخصصينا'           },
                      { icon: Sparkles,     text: 'عرض توضيحي مخصص لعملياتك وقطاعك'           },
                    ]
                  : [
                      { icon: Zap,          text: 'Confirmation within 1 hour'                 },
                      { icon: CalendarCheck, text: 'Discovery call with our specialist'         },
                      { icon: Sparkles,     text: 'Live demo tailored to your operations'      },
                    ]
                ).map((s, i) => (
                  <li key={i} className="flex items-start gap-3.5">
                    <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <s.icon size={13} className="text-primary" />
                    </div>
                    <span className="text-white/55 text-sm leading-snug pt-0.5">{s.text}</span>
                  </li>
                ))}
              </ol>
            </motion.div>

            {/* Response time badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-10 flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-4 py-3 w-fit"
            >
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
              <span className="text-white/55 text-xs">
                {lang === 'ar' ? 'متوسط وقت الرد: أقل من ساعة' : 'Avg. response time: under 1 hour'}
              </span>
            </motion.div>

          </div>
        </div>

        {/* ── RIGHT PANEL: FORM ── */}
        <div className="flex-1 bg-slate-50 flex items-start justify-center py-10 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-12">
          <div className="w-full max-w-2xl">
            <AnimatePresence mode="wait">
              {step === 'form' ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  onSubmit={handleSubmit}
                  className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 lg:p-10 space-y-7"
                >
                  {/* Contact type selector */}
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                      {lang === 'ar' ? 'نوع الطلب' : 'Type of request'}
                    </p>
                    <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                      {contactTypes.map((ct) => {
                        const isActive = form.contactType === ct.key;
                        return (
                          <button
                            type="button"
                            key={ct.key}
                            onClick={() => handleChange('contactType', ct.key)}
                            className={`flex flex-col items-center gap-1.5 sm:gap-2 py-3 sm:py-4 px-1.5 sm:px-3 rounded-2xl border text-center transition-all duration-200 ${
                              isActive
                                ? 'bg-primary/5 border-primary/30 shadow-sm'
                                : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:bg-white'
                            }`}
                          >
                            <ct.icon size={16} className={isActive ? 'text-primary' : 'text-slate-400'} />
                            <span className={`text-[11px] sm:text-xs font-semibold leading-tight ${isActive ? 'text-primary' : 'text-slate-500'}`}>
                              {lang === 'ar' ? ct.labelAr : ct.labelEn}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Name + Company */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">
                        {t('contact.nameLabel')} <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder={t('contact.namePlaceholder')}
                        className={inputClass('name')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">
                        {t('contact.companyLabel')}
                      </label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        placeholder={t('contact.companyPlaceholder')}
                        className={inputClass('company')}
                      />
                    </div>
                  </div>

                  {/* Email + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">
                        {t('contact.emailLabel')} <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder={t('contact.emailPlaceholder')}
                        className={inputClass('email')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">
                        {t('contact.phoneLabel')}
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder={t('contact.phonePlaceholder')}
                        className={inputClass('phone')}
                      />
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-2">
                      {lang === 'ar' ? 'الدولة' : 'Country'}
                    </label>
                    <div className="relative">
                      <select
                        value={form.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                        className={`${inputClass('country')} appearance-none cursor-pointer pr-10`}
                      >
                        <option value="">{lang === 'ar' ? 'اختر دولتك' : 'Select your country'}</option>
                        {COUNTRIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.flag}  {lang === 'ar' ? c.labelAr : c.labelEn}
                          </option>
                        ))}
                      </select>
                      <div className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'} text-slate-400`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                    </div>
                  </div>

                  {/* Industry */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-3">
                      {t('contact.industryLabel')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {industries.map((ind) => {
                        const isActive = form.industry === ind.key;
                        return (
                          <button
                            type="button"
                            key={ind.key}
                            onClick={() => handleChange('industry', ind.key)}
                            className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 ${
                              isActive
                                ? 'bg-primary text-white border-primary shadow-sm'
                                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary hover:bg-white'
                            }`}
                          >
                            {lang === 'ar' ? ind.labelAr : ind.labelEn}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-2">
                      {t('contact.messageLabel')} <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder={t('contact.messagePlaceholder')}
                      rows={5}
                      className={`${inputClass('message')} resize-none`}
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 rounded-2xl bg-primary text-white font-bold text-sm hover:bg-primary-hover transition-all flex items-center justify-center gap-2.5 disabled:opacity-70 shadow-primary-glow"
                  >
                    {loading ? (
                      <><Loader size={16} className="animate-spin" />{t('contact.submitting')}</>
                    ) : (
                      <>{t('contact.submit')}<ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} /></>
                    )}
                  </button>

                  <p className="text-center text-[11px] text-slate-400">
                    {lang === 'ar'
                      ? 'بإرسال هذا النموذج، أنت توافق على سياسة الخصوصية الخاصة بنا.'
                      : 'By submitting, you agree to our privacy policy. No spam, ever.'}
                  </p>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-3xl border border-slate-100 shadow-sm p-12 flex flex-col items-center text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
                    className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mb-6"
                  >
                    <CheckCircle size={38} className="text-emerald-500" />
                  </motion.div>
                  <h3 className="text-slate-900 font-black text-2xl mb-3">
                    {t('contact.successTitle')}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed max-w-sm mb-8">
                    {t('contact.successMsg')}
                  </p>
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 mb-8">
                    <Clock size={14} className="text-primary flex-shrink-0" />
                    <span className="text-slate-600 text-xs">
                      {lang === 'ar' ? 'سنتواصل معك خلال ساعة واحدة' : "We'll reach out within 1 hour"}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate('/landing')}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-all"
                  >
                    {lang === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
                    <ArrowRight size={15} className={isRTL ? 'rotate-180' : ''} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>
    </div>
  );
}
